import { useEffect, useRef, useState } from "react";
import { useApolloClient } from "@apollo/client";
import gql from "graphql-tag";

import { createNexusLiveSession } from "../nexusLiveSession";

const WEB_RTC_FRAGMENT = `
  status
  webRtc {
    signalingWssUrl
    iceServers {
      urls
      username
      credential
    }
  }
`;

const START_LIVE_MUTATION = gql`
  mutation StartLiveStream($input: Vehicles_StartLiveStream_Input!) {
    vehicles {
      startLiveStream(input: $input) {
        sessionToken
        status
      }
    }
  }
`;

const START_PLAYBACK_MUTATION = gql`
  mutation StartPlayback($input: Vehicles_StartPlayback_Input!) {
    vehicles {
      startPlayback(input: $input) {
        sessionToken
        status
      }
    }
  }
`;

// One field for both kinds: the session token records whether nexus is serving it as a live stream
// or a playback, so there is no way to ask the wrong endpoint about a session.
const SESSION_QUERY = gql`
  query VideoSession($sessionToken: ID!) {
    vehicles {
      videoSession(sessionToken: $sessionToken) {
        ${WEB_RTC_FRAGMENT}
      }
    }
  }
`;

const KEEPALIVE_MUTATION = gql`
  mutation KeepaliveVideoSession($input: Vehicles_KeepaliveVideoSession_Input!) {
    vehicles {
      keepaliveVideoSession(input: $input) {
        success
      }
    }
  }
`;

const STOP_MUTATION = gql`
  mutation StopVideoSession($input: Vehicles_StopVideoSession_Input!) {
    vehicles {
      stopVideoSession(input: $input) {
        success
      }
    }
  }
`;

/**
 * Drives one nexus video session — live or playback — into a <video> element.
 *
 * The two differ only in how they start: live takes a camera, playback takes a camera and a moment
 * on the timeline. Everything after that (poll for the WebRTC bundle, connect, keep alive, stop) is
 * the same session state machine in ../nexusLiveSession.
 *
 * @param mode "live" or "playback"
 * @param playFrom required for playback: the moment to start from. A playback is a period, not a
 *   file — nexus works out which recordings cover it and runs on across them.
 */
export default function useNexusVideoSession({
  mode,
  vehicleId,
  cameraId,
  playFrom,
  videoRef,
  play,
  restartKey = 0,
}) {
  const client = useApolloClient();
  const [state, setState] = useState("idle");
  const [statusText, setStatusText] = useState("Ready");
  const sessionRef = useRef(null);

  const isPlayback = mode === "playback";

  useEffect(() => {
    if (!play) return undefined;
    if (isPlayback && !playFrom) return undefined;

    const transport = {
      start: () =>
        client
          .mutate({
            mutation: isPlayback ? START_PLAYBACK_MUTATION : START_LIVE_MUTATION,
            variables: {
              input: isPlayback
                ? { vehicleId, cameraId, at: playFrom }
                : { vehicleId, cameraId },
            },
          })
          .then((r) =>
            isPlayback ? r.data.vehicles.startPlayback : r.data.vehicles.startLiveStream
          ),

      status: (sessionToken) =>
        client
          .query({
            query: SESSION_QUERY,
            variables: { sessionToken },
            fetchPolicy: "no-cache",
          })
          .then((r) => r.data.vehicles.videoSession),

      keepalive: (sessionToken) =>
        client.mutate({
          mutation: KEEPALIVE_MUTATION,
          variables: { input: { sessionToken } },
        }),

      // keepalive lets the request outlive the page: without it the browser cancels the stop fired
      // from `pagehide` and the session lingers on nexus until its own timeout reaps it.
      stop: (sessionToken) =>
        client.mutate({
          mutation: STOP_MUTATION,
          variables: { input: { sessionToken } },
          context: { fetchOptions: { keepalive: true } },
        }),
    };

    const session = createNexusLiveSession({
      transport,
      video: videoRef.current,
      // Nexus has no playback keepalive: a playback runs to its end or is stopped.
      keepalive: !isPlayback,
      // Polling stops for good once the stream is live, so a normal server-side end (30-min cap,
      // ignition off, or the recordings running out) only shows up as the connection dropping —
      // ask the server before painting that as an error.
      endedOnDisconnect: true,
      onState: (next, text) => {
        setState(next);
        setStatusText(text);
      },
    });

    sessionRef.current = session;
    session.start();

    return () => {
      session.stop();
      sessionRef.current = null;
    };
  }, [
    play,
    restartKey,
    isPlayback,
    mode,
    vehicleId,
    cameraId,
    playFrom,
    client,
    videoRef,
  ]);

  useEffect(() => {
    const onPageHide = () => sessionRef.current?.stop();
    window.addEventListener("pagehide", onPageHide);
    return () => window.removeEventListener("pagehide", onPageHide);
  }, []);

  return { state, statusText };
}
